import { useState, useCallback, useRef, useEffect } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { usePrescriptionHover } from "./PrescriptionHoverContext";
import ZoomableDocument from "./ZoomableDocument";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const PolygonElement = ({ polygon, onPolygonHover, onPolygonUnhover, registerPolygonElement }) => {
  const polygonRef = useRef(null);

  useEffect(() => {
    const element = polygonRef.current;
    if (!element) return;
    const cleanupFunctions = polygon.codes.map((code) => registerPolygonElement(code, element));
    return () => cleanupFunctions.forEach((cleanup) => cleanup());
  }, [polygon.codes, registerPolygonElement]);

  return (
    <polygon
      ref={polygonRef}
      className={polygon.className + " pointer-events-auto"}
      points={polygon.pointsString}
      onMouseOver={() => onPolygonHover(polygon.codes)}
      onMouseOut={() => onPolygonUnhover(polygon.codes)}
    />
  );
};

const PDFPage = ({ pdfDocument, pageNumber, height, id, polygons, onPolygonHover, onPolygonUnhover, registerPolygonElement }) => {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);
  const [renderState, setRenderState] = useState({ width: 0, height: 0, cssWidth: 0, polygons: [] });

  const renderPage = useCallback(async () => {
    if (!pdfDocument || !canvasRef.current) return;

    try {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      const page = await pdfDocument.getPage(pageNumber);
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");
      const baseViewport = page.getViewport({ scale: 1 });
      const cssScale = height / baseViewport.height;
      const devicePixelRatio = window.devicePixelRatio || 1;
      const viewport = page.getViewport({ scale: cssScale * devicePixelRatio });

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      renderTaskRef.current = page.render({ canvasContext: context, viewport });
      await renderTaskRef.current.promise;
      renderTaskRef.current = null;

      const pagePolygons = (polygons || [])
        .filter((polygon) => (polygon.pageNumber || 1) === pageNumber && polygon.points?.length)
        .map((polygon) => ({
          id: polygon.id,
          pointsString: polygon.points.map((point) => `${point.x * viewport.width},${point.y * viewport.height}`).join(" "),
          className: polygon.className,
          codes: polygon.codes || [],
        }));

      setRenderState({ width: viewport.width, height: viewport.height, cssWidth: baseViewport.width * cssScale, polygons: pagePolygons });
    } catch (error) {
      if (error?.name === "RenderingCancelledException") return;
      console.error("Error rendering PDF page:", error);
    }
  }, [pdfDocument, pageNumber, height, polygons]);

  useEffect(() => {
    renderPage();
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [renderPage]);

  return (
    <div className="relative inline-block mb-4" style={{ width: renderState.cssWidth || "auto" }}>
      <canvas ref={canvasRef} className={`prescription-viewer-${id}`} style={{ display: "block", width: "100%", height: "auto" }} />
      {renderState.width > 0 && renderState.height > 0 && (
        <svg
          className="absolute top-0 left-0 z-40 pointer-events-none"
          style={{ width: "100%", height: "100%" }}
          viewBox={`0 0 ${renderState.width} ${renderState.height}`}
          preserveAspectRatio="none"
        >
          <defs>
            <pattern id="action-removed-stripes" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" className="stroke-red-500" strokeWidth="4" />
            </pattern>
            <pattern id="action-removed-stripes-hover" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="8" className="stroke-blue-600" strokeWidth="4" />
            </pattern>
          </defs>
          {renderState.polygons.map((polygon, pIndex) => (
            <PolygonElement key={pIndex} polygon={polygon} onPolygonHover={onPolygonHover} onPolygonUnhover={onPolygonUnhover} registerPolygonElement={registerPolygonElement} />
          ))}
        </svg>
      )}
    </div>
  );
};

const PrescriptionViewer = ({ id, imgUrl, polygons = [], heightRatio = 1 }) => {
  const { onPolygonHover, onPolygonUnhover, registerPolygonElement } = usePrescriptionHover();
  const [pdfDocument, setPdfDocument] = useState(null);
  const [error, setError] = useState(null);
  const [imageState, setImageState] = useState({ width: 0, height: 0, polygons: [] });
  const [height, setHeight] = useState(window.innerHeight * heightRatio);
  const rootRef = useRef(null);
  const loadingTaskRef = useRef(null);

  const isPdf = typeof imgUrl === "string" && imgUrl.split(/[?#]/)[0].toLowerCase().endsWith(".pdf");

  useEffect(() => {
    if (!isPdf || !imgUrl) {
      setPdfDocument(null);
      setError(null);
      return;
    }

    let isCancelled = false;

    const loadPdf = async () => {
      try {
        setError(null);
        setPdfDocument(null);
        if (loadingTaskRef.current) {
          await loadingTaskRef.current.destroy();
          loadingTaskRef.current = null;
        }

        loadingTaskRef.current = pdfjsLib.getDocument(imgUrl);
        const pdf = await loadingTaskRef.current.promise;
        if (isCancelled) {
          pdf.destroy();
          return;
        }
        setPdfDocument(pdf);
      } catch (err) {
        if (isCancelled) return;
        console.error("Error loading PDF:", err);
        setError(err);
        setPdfDocument(null);
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (loadingTaskRef.current) {
        loadingTaskRef.current.destroy();
        loadingTaskRef.current = null;
      }
    };
  }, [imgUrl, isPdf]);

  useEffect(() => () => pdfDocument?.destroy(), [pdfDocument]);

  const calculateImagePolygons = useCallback(() => {
    if (!imgUrl || !rootRef.current) return;

    const imageElement = rootRef.current.querySelector(`.prescription-viewer-${id}`);
    if (!imageElement) return;

    const displayedWidth = imageElement.clientWidth;
    const displayedHeight = imageElement.clientHeight;
    if (!displayedWidth || !displayedHeight) return;

    const formattedPolygons = (polygons || [])
      .filter((polygon) => polygon.points?.length)
      .map((polygon) => ({
        id: polygon.id,
        pointsString: polygon.points.map((point) => `${point.x * displayedWidth},${point.y * displayedHeight}`).join(" "),
        className: polygon.className,
        pageNumber: polygon.pageNumber || 1,
        codes: polygon.codes || [],
      }));

    setImageState({ width: displayedWidth, height: displayedHeight, polygons: formattedPolygons });
  }, [id, imgUrl, polygons]);

  useEffect(() => {
    if (isPdf || !rootRef.current) return;

    const imageElement = rootRef.current.querySelector(`.prescription-viewer-${id}`);
    if (!imageElement) return;

    let scheduledFrameId = null;

    const resizeObserver = new ResizeObserver(() => {
      if (scheduledFrameId !== null) cancelAnimationFrame(scheduledFrameId);
      scheduledFrameId = requestAnimationFrame(() => {
        calculateImagePolygons();
        scheduledFrameId = null;
      });
    });

    resizeObserver.observe(imageElement);

    if (imageElement.complete && imageElement.clientWidth > 0 && imageElement.clientHeight > 0) {
      calculateImagePolygons();
    }

    return () => {
      resizeObserver.disconnect();
      if (scheduledFrameId !== null) cancelAnimationFrame(scheduledFrameId);
    };
  }, [id, calculateImagePolygons, isPdf]);

  useEffect(() => {
    const handleResize = () => setHeight(window.innerHeight * heightRatio);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [heightRatio]);

  if (!imgUrl) return null;

  return (
    <div className="z-0">
      <div ref={rootRef} className="relative">
        <div className="relative overflow-hidden">
          <ZoomableDocument key={id}>
            {isPdf ? (
              <>
                {!pdfDocument && !error && (
                  <div className="flex items-center justify-center" style={{ height: height }}>
                    Chargement du PDF...
                  </div>
                )}
                {error && (
                  <div className="flex items-center justify-center text-red-500" style={{ height: height }}>
                    Erreur lors du chargement du PDF
                  </div>
                )}
                {pdfDocument && (
                  <div className="grid grid-cols-1 justify-center items-center">
                    {Array.from({ length: pdfDocument.numPages }, (_, index) => (
                      <PDFPage
                        key={index}
                        pdfDocument={pdfDocument}
                        pageNumber={index + 1}
                        height={height}
                        id={id}
                        polygons={polygons}
                        onPolygonHover={onPolygonHover}
                        onPolygonUnhover={onPolygonUnhover}
                        registerPolygonElement={registerPolygonElement}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="relative w-full">
                <img
                  src={imgUrl}
                  alt="Ordonnance"
                  className={`prescription-viewer-${id}`}
                  style={{ display: "block", width: "100%", height: "auto" }}
                  onLoad={calculateImagePolygons}
                />
                {imageState.width > 0 && imageState.height > 0 && (
                  <svg
                    className="absolute top-0 left-0 z-[220] pointer-events-none"
                    style={{ width: "100%", height: "100%" }}
                    viewBox={`0 0 ${imageState.width} ${imageState.height}`}
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <pattern id="action-removed-stripes" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                        <line x1="0" y1="0" x2="0" y2="8" className="stroke-red-500" strokeWidth="4" />
                      </pattern>
                      <pattern id="action-removed-stripes-hover" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                        <line x1="0" y1="0" x2="0" y2="8" className="stroke-blue-600" strokeWidth="4" />
                      </pattern>
                    </defs>
                    {imageState.polygons.map((polygon, pIndex) => (
                      <PolygonElement
                        key={pIndex}
                        polygon={polygon}
                        onPolygonHover={onPolygonHover}
                        onPolygonUnhover={onPolygonUnhover}
                        registerPolygonElement={registerPolygonElement}
                      />
                    ))}
                  </svg>
                )}
              </div>
            )}
          </ZoomableDocument>
        </div>
      </div>
    </div>
  );
};

export default PrescriptionViewer;
